/**
 * Campaign attribution parameters read from the URL the visitor landed on.
 *
 * The SPA rewrites the URL almost immediately after landing (`RequireAuth`
 * redirects to `/login`, the index route redirects to the team's projects —
 * both with `replace`), and PostHog only initialises after `measurement`
 * consent is granted. For a first-time visitor the parameters are therefore
 * gone from `location` by the time the SDK could read them. This module
 * snapshots them at module evaluation — which happens while `main.tsx`'s
 * import graph loads, before the first render and hence before any redirect
 * — so `initPostHog` can hand them to the SDK whenever consent arrives.
 *
 * A signed-out visitor additionally leaves the page for WorkOS and returns on
 * `/auth/callback` in a fresh page load. The snapshot rides along in the OAuth
 * `state` (`landingParamsToSignInState` on the way out,
 * `adoptLandingParamsFromSignInState` on the way back) — no device storage
 * before consent.
 *
 * `utm_*` are the standard channel fields; `ref` names the NOMA property that
 * relayed the visitor (e.g. `noma-artifact-<slug>`), see issue #1666.
 */
const LANDING_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'ref',
] as const;

const SIGN_IN_STATE_KEY = 'landing';

type LandingParams = Record<string, string>;

function parseLandingParams(search: string): LandingParams {
  const query = new URLSearchParams(search);
  const params: LandingParams = {};
  for (const key of LANDING_PARAM_KEYS) {
    const value = query.get(key);
    if (value) params[key] = value;
  }
  return params;
}

let landingParams: LandingParams = parseLandingParams(
  typeof window === 'undefined' ? '' : window.location.search,
);

/** The landing URL's attribution parameters (snapshotted at app boot). */
export function getLandingParams(): Readonly<LandingParams> {
  return landingParams;
}

/** OAuth `state` payload carrying the snapshot through the WorkOS round-trip. */
export function landingParamsToSignInState(): Record<string, unknown> | undefined {
  return Object.keys(landingParams).length > 0
    ? { [SIGN_IN_STATE_KEY]: landingParams }
    : undefined;
}

/**
 * Restore the snapshot from the OAuth `state` WorkOS echoed back. The state
 * comes off the URL, so it is validated: only known keys with non-empty string
 * values are adopted.
 */
export function adoptLandingParamsFromSignInState(state: unknown): void {
  if (typeof state !== 'object' || state === null) return;
  const landing: unknown = (state as Record<string, unknown>)[SIGN_IN_STATE_KEY];
  if (typeof landing !== 'object' || landing === null) return;
  const adopted: LandingParams = {};
  for (const key of LANDING_PARAM_KEYS) {
    const value = (landing as Record<string, unknown>)[key];
    if (typeof value === 'string' && value) adopted[key] = value;
  }
  if (Object.keys(adopted).length > 0) landingParams = { ...landingParams, ...adopted };
}
